import { styled } from "goober";
import { mobileMedia, useIsMobile } from "../../hooks/useIsMobile";
import { connectorsByIdentifier } from "../../../shared/connectors";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import {
  pickChatExamples,
  plainPrompt,
  promptSegments,
} from "./chatExamplePrompts";

const Overlay = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: none;
  overflow-y: auto;
  @media (max-height: 800px) {
    padding: 16px 24px;
  }
  ${mobileMedia} {
    padding: 16px;
  }
`;

const Inner = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 600px;
  /* centers like align/justify center, but degrades to scrolling instead of
     clipping the top when the content is taller than a small viewport */
  margin: auto;
  ${mobileMedia} {
    gap: 16px;
  }
  @media (max-height: 800px) {
    gap: 16px;
  }
  pointer-events: auto;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  &[data-visible="false"] {
    opacity: 0;
    transform: translateY(-4px);
    pointer-events: none;
  }
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;

  ${mobileMedia} {
    gap: 4px;
  }
`;

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 240px));
  grid-auto-rows: 56px;
  justify-content: center;
  gap: 8px;
  width: 100%;
  transition: opacity 180ms ease-out;
  &[data-ready="false"] {
    opacity: 0;
  }
  ${mobileMedia} {
    grid-template-columns: 240px;
  }
  @container (max-width: 560px) {
    grid-template-columns: minmax(0, 320px);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Card = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  text-align: left;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  cursor: pointer;
  &:hover {
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const IconSlot = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const Logo = styled("img")`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
`;

const PromptText = styled("div")`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
`;

type Props = {
  onPick: (prompt: string) => void;
  iconColor: string;
  userName: string;
  seed: string;
  visible: boolean;
  content?: {
    title: string;
    description: string;
    prompts: readonly string[];
  };
};

export function greetingForHour(
  hour: number
): "Morning" | "Afternoon" | "Evening" {
  if (hour < 12) return "Morning";
  if (hour < 18) return "Afternoon";
  return "Evening";
}

// A user with no display name has `name` defaulted to their email, so an
// email-looking name means there is no real name to greet them by.
export function greetingName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed.includes("@")) return "";
  return trimmed.split(" ")[0] ?? "";
}

export function NewChatEmptyState({
  onPick,
  iconColor,
  userName,
  seed,
  visible,
  content,
}: Props) {
  const [connectors, details] = useQuery(queries.connectorsIndex());
  const isMobile = useIsMobile();

  // Fewer cards on mobile: six stacked single-column cards plus the header
  // rarely fit a phone viewport.
  const exampleCount = isMobile ? 4 : 6;
  const identifiers = [...new Set(connectors.map((c) => c.identifier))];
  const ready = content != null || details.type === "complete";
  const examples = content
    ? content.prompts
        .slice(0, exampleCount)
        .map((prompt) => ({ prompt, connectors: [] as string[] }))
    : ready
      ? pickChatExamples(identifiers, exampleCount, seed)
      : [];

  const greeting = greetingForHour(new Date().getHours());
  const title = userName
    ? `${greeting}, ${userName}`
    : `Good ${greeting.toLowerCase()}`;

  return (
    <Overlay>
      <Inner data-visible={visible} inert={!visible}>
        <Header>
          <Icon
            name="ask"
            size={isMobile ? 24 : 36}
            color={iconColor}
            style={isMobile ? undefined : { transform: "translateY(-8px)" }}
          />
          <Text
            variant="display-md"
            color="var(--text-primary)"
            style={isMobile ? { fontSize: 14, lineHeight: "20px" } : undefined}
          >
            {content?.title ?? title}
          </Text>
          <Text
            variant="text-regular"
            color="var(--text-secondary)"
            style={{ maxWidth: 360 }}
          >
            {content?.description ??
              "Chat is your lightweight personal space for quick tasks and questions — plugged into all your connectors."}
          </Text>
        </Header>
        <Grid data-ready={ready}>
          {examples.map((example) => {
            const lead = example.connectors[0];
            const logo = lead ? connectorsByIdentifier[lead]?.logo : undefined;
            return (
              <Card
                key={example.prompt}
                data-no-pill
                onPress={() => onPick(plainPrompt(example.prompt))}
              >
                <IconSlot>
                  {logo ? (
                    <Logo src={logo} alt="" />
                  ) : (
                    <Icon
                      name={content ? "scan-heart" : "sparkles"}
                      size={16}
                      color="var(--text-secondary)"
                    />
                  )}
                </IconSlot>
                <PromptText>
                  {promptSegments(example.prompt).map((segment) => (
                    <Text
                      key={segment.text}
                      variant={
                        segment.bold ? "text-regular-md" : "text-regular"
                      }
                      color="var(--text-primary)"
                    >
                      {segment.text}
                    </Text>
                  ))}
                </PromptText>
              </Card>
            );
          })}
        </Grid>
      </Inner>
    </Overlay>
  );
}
